import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import { crmAPI, adminAPI } from "../api";
import { getUserErrorMessage } from "../utils/errors";
import { calendarConfig } from "../constants/calendarConfig";
import MIcon from "../icons/MIcon";
import SpinnerIcon from "../icons/SpinnerIcon";
import MessageBanner from "../components/ui/MessageBanner";
import {
  getMonday,
  addDays,
  formatDateISO,
  isSameDay,
  parseDateTime,
  getBrowserTimeZone,
  getDateInTimeZone,
  getDateTimePartsInTimeZone,
  formatDateInTimeZone,
  zonedDateTimeToIso,
} from "../utils/dateTimeUtils";

const Calendar = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language;

  // Org selection
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgsLoading, setOrgsLoading] = useState(false);

  // Calendar state
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [businessTimeZone, setBusinessTimeZone] = useState(null);
  const [useBrowserTimeZone, setUseBrowserTimeZone] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Create event form
  const [newEventDate, setNewEventDate] = useState("");
  const [newEventStartTime, setNewEventStartTime] = useState("09:00");
  const [newEventEndTime, setNewEventEndTime] = useState("10:00");
  const [newEventPhone, setNewEventPhone] = useState("");
  const [newEventReason, setNewEventReason] = useState("");
  const [newEventMeta, setNewEventMeta] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [createMsg, setCreateMsg] = useState({ text: "", type: "" });

  // Block days form
  const [blockStartDate, setBlockStartDate] = useState("");
  const [blockEndDate, setBlockEndDate] = useState("");
  const [blockLoading, setBlockLoading] = useState(false);
  const [blockMsg, setBlockMsg] = useState({ text: "", type: "" });

  // Cancel loading
  const [cancelLoading, setCancelLoading] = useState(false);

  const effectiveOrgId = user?.is_admin ? selectedOrgId : user?.org_id;
  const browserTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const displayTimeZone =
    (useBrowserTimeZone ? browserTimeZone : businessTimeZone) || browserTimeZone;

  // Week days array
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const today = useMemo(
    () => getDateInTimeZone(new Date(), displayTimeZone),
    [displayTimeZone],
  );

  // Load orgs for admin
  useEffect(() => {
    if (user?.is_admin) {
      setOrgsLoading(true);
      adminAPI
        .listOrgs()
        .then((res) => {
          setOrgs(res.data);
          if (res.data.length > 0 && !selectedOrgId) {
            setSelectedOrgId(res.data[0].id);
          }
        })
        .catch(() => {})
        .finally(() => setOrgsLoading(false));
    }
  }, [user, selectedOrgId]);

  // Load the business timezone whenever the viewed organization changes.
  // Calendar mode intentionally resets to business time for each organization.
  useEffect(() => {
    if (!effectiveOrgId) {
      setBusinessTimeZone(null);
      setEvents([]);
      setEventsLoading(false);
      return undefined;
    }

    let ignore = false;
    setBusinessTimeZone(null);
    setUseBrowserTimeZone(false);
    setEvents([]);
    setEventsLoading(true);

    crmAPI
      .getConfig(effectiveOrgId)
      .then((res) => {
        if (ignore) return;
        const timeZone =
          res.data?.appointments?.timezone || calendarConfig.DEFAULT_TIME_ZONE;
        setBusinessTimeZone(timeZone);
        setWeekStart(getMonday(getDateInTimeZone(new Date(), timeZone)));
      })
      .catch(() => {
        if (ignore) return;
        setBusinessTimeZone(calendarConfig.DEFAULT_TIME_ZONE);
        setWeekStart(
          getMonday(
            getDateInTimeZone(new Date(), calendarConfig.DEFAULT_TIME_ZONE),
          ),
        );
      });

    return () => {
      ignore = true;
    };
  }, [effectiveOrgId, browserTimeZone]);

  // Load events
  const loadEvents = useCallback(async () => {
    if (!effectiveOrgId || !businessTimeZone) return;
    setEventsLoading(true);
    try {
      // Include the adjacent UTC dates because timezone conversion can move an
      // event across the edge of the displayed week.
      const queryStart = addDays(weekStart, -1);
      const queryEnd = addDays(weekStart, 7);
      const res = await crmAPI.getEvents(effectiveOrgId, {
        start_date: formatDateISO(queryStart),
        end_date: formatDateISO(queryEnd),
      });
      setEvents(res.data);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [effectiveOrgId, weekStart, businessTimeZone]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // ── Navigation ──

  const goToPreviousWeek = () => setWeekStart((prev) => addDays(prev, -7));
  const goToNextWeek = () => setWeekStart((prev) => addDays(prev, 7));
  const goToToday = () => setWeekStart(getMonday(today));

  // ── Week header label ──
  const weekLabel = useMemo(() => {
    const end = addDays(weekStart, 6);
    const startMonth = weekStart.toLocaleDateString(locale, { month: "long" });
    const endMonth = end.toLocaleDateString(locale, { month: "long" });
    if (weekStart.getMonth() === end.getMonth()) {
      return `${weekStart.getDate()} – ${end.getDate()} ${startMonth} ${weekStart.getFullYear()}`;
    }
    return `${weekStart.getDate()} ${startMonth} – ${end.getDate()} ${endMonth} ${end.getFullYear()}`;
  }, [weekStart, locale]);

  // ── Events per day ──
  const eventsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => {
      map[formatDateISO(d)] = [];
    });
    events.forEach((ev) => {
      const key = formatDateInTimeZone(ev.start_datetime, displayTimeZone);
      if (map[key]) {
        map[key].push(ev);
      }
    });
    return map;
  }, [events, weekDays, displayTimeZone]);

  // ── Blocked days set ──
  const blockedDays = useMemo(() => {
    const set = new Set();
    events.forEach((ev) => {
      if (ev.is_blocked && !ev.cancelled_at) {
        set.add(formatDateInTimeZone(ev.start_datetime, displayTimeZone));
      }
    });
    return set;
  }, [events, displayTimeZone]);

  // ── Create event ──

  const openCreateModal = () => {
    setNewEventDate(formatDateInTimeZone(new Date(), displayTimeZone));
    setNewEventStartTime("09:00");
    setNewEventEndTime("10:00");
    setNewEventPhone("");
    setNewEventReason("");
    setNewEventMeta([]);
    setCreateMsg({ text: "", type: "" });
    setShowCreateModal(true);
  };

  const handleCreateEvent = async () => {
    setCreateMsg({ text: "", type: "" });

    if (!newEventDate || !newEventPhone.trim() || !newEventReason.trim()) {
      setCreateMsg({
        text: t("calendar.errors.fillRequired"),
        type: "error",
      });
      return;
    }

    const startDt = zonedDateTimeToIso(
      newEventDate,
      newEventStartTime,
      displayTimeZone,
    );
    const endDt = zonedDateTimeToIso(
      newEventDate,
      newEventEndTime,
      displayTimeZone,
    );

    if (endDt <= startDt) {
      setCreateMsg({
        text: t("calendar.errors.endAfterStart"),
        type: "error",
      });
      return;
    }

    // Build metadata object from key-value pairs
    const metaObj = {};
    newEventMeta.forEach(({ key, value }) => {
      if (key.trim()) {
        metaObj[key.trim()] = value.trim();
      }
    });

    setCreateLoading(true);
    try {
      await crmAPI.createEvent(effectiveOrgId, {
        start_datetime: startDt,
        end_datetime: endDt,
        phone: newEventPhone.trim(),
        reason: newEventReason.trim(),
        event_meta: Object.keys(metaObj).length > 0 ? metaObj : null,
      });
      setShowCreateModal(false);
      loadEvents();
    } catch (err) {
      setCreateMsg({
        text: getUserErrorMessage(err, "create"),
        type: "error",
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const addMetaField = () => {
    setNewEventMeta((prev) => [...prev, { key: "", value: "" }]);
  };

  const updateMetaField = (index, field, value) => {
    setNewEventMeta((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const removeMetaField = (index) => {
    setNewEventMeta((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Block days ──

  const openBlockModal = () => {
    setBlockStartDate("");
    setBlockEndDate("");
    setBlockMsg({ text: "", type: "" });
    setShowBlockModal(true);
  };

  const handleBlockDays = async () => {
    setBlockMsg({ text: "", type: "" });

    if (!blockStartDate || !blockEndDate) {
      setBlockMsg({ text: t("calendar.errors.selectBothDates"), type: "error" });
      return;
    }
    if (blockEndDate < blockStartDate) {
      setBlockMsg({
        text: t("calendar.errors.endDateAfterStart"),
        type: "error",
      });
      return;
    }

    setBlockLoading(true);
    try {
      await crmAPI.blockDays(effectiveOrgId, {
        start_date: blockStartDate,
        end_date: blockEndDate,
      });
      setShowBlockModal(false);
      loadEvents();
    } catch (err) {
      setBlockMsg({
        text: getUserErrorMessage(err, "save"),
        type: "error",
      });
    } finally {
      setBlockLoading(false);
    }
  };

  // ── Cancel event ──

  const handleCancelEvent = async () => {
    if (!showEventDetail) return;
    setCancelLoading(true);
    try {
      await crmAPI.cancelEvent(effectiveOrgId, showEventDetail.id);
      setShowCancelConfirm(false);
      setShowEventDetail(null);
      loadEvents();
    } catch {
      // Error handling - event might already be cancelled
    } finally {
      setCancelLoading(false);
    }
  };

  // ── Event position calc ──

  const getEventStyle = (ev) => {
    const start = parseDateTime(ev.start_datetime);
    const end = parseDateTime(ev.end_datetime);
    const startParts = getDateTimePartsInTimeZone(start, displayTimeZone);
    const startMinutes = startParts.hour * 60 + startParts.minute;
    const durationMinutes = (end.getTime() - start.getTime()) / 60000;
    const topOffset =
      ((startMinutes - calendarConfig.HOUR_START * 60) / 60) * 60; // 60px per hour
    const height = durationMinutes;
    return {
      top: `${Math.max(0, topOffset)}px`,
      height: `${Math.max(20, height)}px`,
    };
  };

  // ── Render ──

  return (
    <div className="h-screen flex flex-col px-4 py-6 sm:px-8 lg:px-12 overflow-hidden">
      <div className="max-w-6xl w-full mx-auto flex flex-col flex-1 min-h-0">
        {/* Page header */}
        <div className="mb-6 flex-none">
          <h1 className="font-display text-3xl font-semibold text-on-surface-light dark:text-on-surface tracking-tight mb-2">
            {t("nav.calendar")}
          </h1>
          <p className="text-sm text-on-surface-light-variant dark:text-on-surface-variant">
            {t("calendar.subtitle")}
          </p>
        </div>

        {/* Admin org selector */}
        {user?.is_admin && (
          <div className="mb-6 flex-none">
          <div className="relative w-full sm:w-80">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none z-10">
              <MIcon name="corporate_fare" className="sm" />
            </div>
            <select
              value={selectedOrgId || ""}
              onChange={(e) => setSelectedOrgId(parseInt(e.target.value))}
              disabled={orgsLoading}
              className="auth-input glass-select w-full pl-11 pr-10 py-3 rounded-lg text-sm font-body appearance-none cursor-pointer"
            >
              <option value="" disabled>
                {orgsLoading ? t("calendar.loadingShort") : t("calendar.selectOrg")}
              </option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.orgname}
                </option>
              ))}
            </select>
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
              <MIcon name="expand_more" className="sm" />
            </div>
          </div>
        </div>
      )}

      {!effectiveOrgId ? (
        <div className="flex-1 flex items-center justify-center text-center text-on-surface-light-variant dark:text-on-surface-variant text-sm">
          {t("calendar.selectOrgPrompt")}
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Week navigation */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3 flex-none">
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousWeek}
                className="w-9 h-9 rounded-lg bg-surface-light-container dark:bg-surface-container-high border border-surface-light-highest dark:border-white/5 flex items-center justify-center text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface hover:bg-surface-light-high dark:hover:bg-surface-bright transition-colors"
                title={t("calendar.prevWeek")}
              >
                <MIcon name="chevron_left" />
              </button>
              <button
                onClick={goToToday}
                className="px-3 py-1.5 rounded-lg bg-surface-light-container dark:bg-surface-container-high border border-surface-light-highest dark:border-white/5 text-sm text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface hover:bg-surface-light-high dark:hover:bg-surface-bright transition-colors font-medium"
              >
                {t("calendar.today")}
              </button>
              <button
                onClick={goToNextWeek}
                className="w-9 h-9 rounded-lg bg-surface-light-container dark:bg-surface-container-high border border-surface-light-highest dark:border-white/5 flex items-center justify-center text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface hover:bg-surface-light-high dark:hover:bg-surface-bright transition-colors"
                title={t("calendar.nextWeek")}
              >
                <MIcon name="chevron_right" />
              </button>
              <h2 className="font-display text-lg font-semibold text-on-surface-light dark:text-on-surface ml-3">
                {weekLabel}
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div
                className="flex items-center rounded-lg border border-surface-light-highest dark:border-white/5 bg-surface-light-container dark:bg-surface-container-high p-1"
                role="group"
                aria-label={t("calendar.timeZoneSelector")}
              >
                <span className="px-2 text-on-surface-light-variant dark:text-on-surface-variant" aria-hidden="true">
                  <MIcon name="schedule" className="sm" />
                </span>
                <button
                  type="button"
                  onClick={() => setUseBrowserTimeZone(false)}
                  aria-pressed={!useBrowserTimeZone}
                  title={t("calendar.businessTimeZoneTitle", {
                    timeZone: businessTimeZone || browserTimeZone,
                  })}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                    !useBrowserTimeZone
                      ? "bg-white dark:bg-surface-bright text-brand dark:text-on-surface shadow-sm"
                      : "text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface"
                  }`}
                >
                  {t("calendar.businessTime")}
                </button>
                <button
                  type="button"
                  onClick={() => setUseBrowserTimeZone(true)}
                  aria-pressed={useBrowserTimeZone}
                  title={t("calendar.browserTimeZoneTitle", {
                    timeZone: browserTimeZone,
                  })}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 ${
                    useBrowserTimeZone
                      ? "bg-white dark:bg-surface-bright text-brand dark:text-on-surface shadow-sm"
                      : "text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface"
                  }`}
                >
                  {t("calendar.browserTime")}
                </button>
              </div>
              <button
                onClick={openCreateModal}
                className="bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 shadow-brand-glow flex items-center gap-2 text-sm active:scale-95"
              >
                <MIcon name="add" className="sm" />
                <span>{t("calendar.newEvent")}</span>
              </button>
              <button
                onClick={openBlockModal}
                className="py-2 px-4 rounded-lg bg-surface-light-container dark:bg-surface-container-high border border-surface-light-highest dark:border-white/5 text-sm text-on-surface-light-variant dark:text-on-surface-variant hover:text-error-light dark:hover:text-error hover:border-error-light/30 dark:hover:border-error/30 hover:bg-error-light/5 dark:hover:bg-error/5 transition-all duration-200 font-medium flex items-center gap-2"
              >
                <MIcon name="block" className="sm" />
                <span>{t("calendar.blockDays")}</span>
              </button>
            </div>
          </div>

          {eventsLoading ? (
            <div className="flex-1 flex justify-center items-center">
              <SpinnerIcon />
            </div>
          ) : (
            /* Week grid */
            <div className="flat-card rounded-xl overflow-hidden flex flex-col flex-1 min-h-0">
              <div className="overflow-auto flex-1 min-h-0">
                {/* Day headers */}
                <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-surface-light-highest dark:border-white/5 sticky top-0 z-20 bg-white dark:bg-[#1A1A1A]">
                  <div className="p-2" /> {/* Time column spacer */}
                  {weekDays.map((day, i) => {
                    const isToday = isSameDay(day, today);
                    return (
                      <div
                        key={i}
                        className={`p-3 text-center border-l border-surface-light-highest dark:border-white/5 ${
                          isToday ? "bg-brand/5 dark:bg-brand/5" : ""
                        }`}
                      >
                        <div className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant uppercase tracking-wider font-semibold">
                          {day.toLocaleDateString(locale, { weekday: "short" })}
                        </div>
                        <div
                          className={`text-lg font-display font-semibold mt-0.5 ${
                            isToday
                              ? "text-brand dark:text-brand"
                              : "text-on-surface-light dark:text-on-surface"
                          }`}
                        >
                          {day.getDate()}
                        </div>
                        <div className="text-[10px] text-on-surface-light-variant dark:text-on-surface-variant">
                          {day.toLocaleDateString(locale, { month: "short" })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Time grid body */}
                <div className="grid grid-cols-[60px_repeat(7,1fr)] pt-3">
                  {/* Time labels column */}
                  <div>
                    {calendarConfig.HOURS.map((hour) => (
                      <div
                        key={hour}
                        className="h-[60px] flex items-start justify-end pr-2 pt-0"
                      >
                        <span className="text-[10px] text-on-surface-light-variant dark:text-on-surface-variant -mt-1.5 font-mono">
                          {String(hour).padStart(2, "0")}:00
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  {weekDays.map((day, dayIndex) => {
                    const dateKey = formatDateISO(day);
                    const dayEvents = eventsByDay[dateKey] || [];
                    const isBlocked = blockedDays.has(dateKey);
                    const isToday = isSameDay(day, today);

                    return (
                      <div
                        key={dayIndex}
                        className={`border-l border-surface-light-highest dark:border-white/5 relative ${
                          isToday ? "bg-brand/[0.02]" : ""
                        }`}
                      >
                        {/* Hour lines */}
                        {calendarConfig.HOURS.map((hour) => (
                          <div
                            key={hour}
                            className="h-[60px] border-t border-surface-light-container dark:border-white/[0.04]"
                          />
                        ))}

                        {/* Blocked overlay */}
                        {isBlocked && (
                          <div className="calendar-blocked-overlay">
                            <span>{t("calendar.blocked")}</span>
                          </div>
                        )}

                        {/* Event tiles */}
                        {dayEvents
                          .filter((ev) => !ev.is_blocked)
                          .map((ev) => {
                            const isCancelled = !!ev.cancelled_at;
                            return (
                              <div
                                key={ev.id}
                                className={`calendar-event-tile ${isCancelled ? "cancelled" : ""}`}
                                style={getEventStyle(ev)}
                                onClick={() =>
                                  !isCancelled && setShowEventDetail(ev)
                                }
                                title={
                                  isCancelled
                                    ? t("calendar.cancelled")
                                    : ev.reason || ev.phone
                                }
                              >
                                <div className="font-semibold text-[11px] truncate">
                                  {ev.reason || ev.phone}
                                </div>
                                {ev.event_meta && (
                                  <div className="text-[10px] opacity-60 mt-0.5 truncate">
                                    {Object.entries(ev.event_meta)
                                      .map(([k, v]) => `${k}: ${v}`)
                                      .join(" | ")}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      </div>

      {/* ── Create Event Modal ── */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="flat-card rounded-xl w-full max-w-lg mx-4 animate-float-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-lg font-semibold text-on-surface-light dark:text-on-surface">
                  {t("calendar.newEvent")}
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors"
                >
                  <MIcon name="close" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("calendar.date")}
                  </label>
                  <input
                    type="date"
                    value={newEventDate}
                    onChange={(e) => setNewEventDate(e.target.value)}
                    className="auth-input w-full px-4 py-2.5 rounded-lg text-sm font-body"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                      {t("calendar.startTime")} ({displayTimeZone})
                    </label>
                    <select
                      value={newEventStartTime}
                      onChange={(e) => setNewEventStartTime(e.target.value)}
                      className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                    >
                      {calendarConfig.TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                      {t("calendar.endTime")} ({displayTimeZone})
                    </label>
                    <select
                      value={newEventEndTime}
                      onChange={(e) => setNewEventEndTime(e.target.value)}
                      className="auth-input glass-select w-full px-4 py-2.5 rounded-lg text-sm font-body appearance-none cursor-pointer"
                    >
                      {calendarConfig.TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("calendar.phone")}
                  </label>
                  <input
                    type="text"
                    value={newEventPhone}
                    onChange={(e) => setNewEventPhone(e.target.value)}
                    placeholder="+40 7XX XXX XXX"
                    className="auth-input w-full px-4 py-2.5 rounded-lg text-sm font-body"
                  />
                </div>

                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("calendar.reason")}
                  </label>
                  <input
                    type="text"
                    value={newEventReason}
                    onChange={(e) => setNewEventReason(e.target.value)}
                    placeholder={t("calendar.reasonPlaceholder")}
                    className="auth-input w-full px-4 py-2.5 rounded-lg text-sm font-body"
                  />
                </div>

                {/* Dynamic metadata */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-on-surface-light-variant dark:text-on-surface-variant font-medium">
                      {t("calendar.extraInfo")}
                    </label>
                    <button
                      type="button"
                      onClick={addMetaField}
                      className="text-xs text-brand dark:text-brand hover:text-brand-light dark:hover:text-primary transition-colors flex items-center gap-1"
                    >
                      <MIcon name="add" className="!text-[14px]" />
                      {t("calendar.addField")}
                    </button>
                  </div>
                  {newEventMeta.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={item.key}
                        onChange={(e) =>
                          updateMetaField(idx, "key", e.target.value)
                        }
                        placeholder={t("calendar.metaKey")}
                        className="auth-input flex-1 px-3 py-2 rounded-lg text-sm font-body"
                      />
                      <input
                        type="text"
                        value={item.value}
                        onChange={(e) =>
                          updateMetaField(idx, "value", e.target.value)
                        }
                        placeholder={t("calendar.metaValue")}
                        className="auth-input flex-1 px-3 py-2 rounded-lg text-sm font-body"
                      />
                      <button
                        type="button"
                        onClick={() => removeMetaField(idx)}
                        className="text-on-surface-light-variant dark:text-on-surface-variant hover:text-error-light dark:hover:text-error transition-colors p-1"
                      >
                        <MIcon name="delete" className="sm" />
                      </button>
                    </div>
                  ))}
                </div>

                <MessageBanner message={createMsg} />

                <button
                  onClick={handleCreateEvent}
                  disabled={createLoading}
                  className="w-full bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white font-semibold py-2.5 rounded-lg transition-all duration-200 shadow-brand-glow disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm active:scale-95"
                >
                  {createLoading ? (
                    <SpinnerIcon />
                  ) : (
                    <MIcon name="add" className="sm" />
                  )}
                  <span>{createLoading ? t("calendar.creating") : t("calendar.create")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Block Days Modal ── */}
      {showBlockModal && (
        <div className="modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div
            className="flat-card rounded-xl w-full max-w-md mx-4 animate-float-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-lg font-semibold text-on-surface-light dark:text-on-surface">
                  {t("calendar.blockDays")}
                </h3>
                <button
                  onClick={() => setShowBlockModal(false)}
                  className="text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors"
                >
                  <MIcon name="close" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("calendar.from")}
                  </label>
                  <input
                    type="date"
                    value={blockStartDate}
                    onChange={(e) => setBlockStartDate(e.target.value)}
                    className="auth-input w-full px-4 py-2.5 rounded-lg text-sm font-body"
                  />
                </div>
                <div>
                  <label className="block text-xs text-on-surface-light-variant dark:text-on-surface-variant mb-1.5 font-medium">
                    {t("calendar.to")}
                  </label>
                  <input
                    type="date"
                    value={blockEndDate}
                    onChange={(e) => setBlockEndDate(e.target.value)}
                    className="auth-input w-full px-4 py-2.5 rounded-lg text-sm font-body"
                  />
                </div>

                <MessageBanner message={blockMsg} />

                <button
                  onClick={handleBlockDays}
                  disabled={blockLoading}
                  className="w-full py-2.5 rounded-lg bg-error-light/10 dark:bg-error/10 border border-error-light/20 dark:border-error/20 text-error-light dark:text-error hover:bg-error-light/20 dark:hover:bg-error/20 font-semibold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {blockLoading ? (
                    <SpinnerIcon />
                  ) : (
                    <MIcon name="block" className="sm" />
                  )}
                  <span>{blockLoading ? t("calendar.blocking") : t("calendar.block")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Event Detail Modal ── */}
      {showEventDetail && (
        <div className="modal-overlay" onClick={() => setShowEventDetail(null)}>
          <div
            className="flat-card rounded-xl w-full max-w-md mx-4 animate-float-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-lg font-semibold text-on-surface-light dark:text-on-surface">
                  {t("calendar.eventDetails")}
                </h3>
                <button
                  onClick={() => setShowEventDetail(null)}
                  className="text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors"
                >
                  <MIcon name="close" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant uppercase tracking-wider">
                      {t("calendar.date")}
                    </span>
                    <p className="text-sm text-on-surface-light dark:text-on-surface font-medium mt-0.5">
                      {new Date(
                        showEventDetail.start_datetime,
                      ).toLocaleDateString(locale, {
                        timeZone: displayTimeZone,
                      })}
                    </p>
                  </div>
                  <div>
                    <span className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant uppercase tracking-wider">
                      {t("calendar.timeRange")}
                    </span>
                    <p className="text-sm text-on-surface-light dark:text-on-surface font-medium mt-0.5">
                      {new Date(
                        showEventDetail.start_datetime,
                      ).toLocaleTimeString(locale, {
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZone: displayTimeZone,
                      })}
                      {" – "}
                      {new Date(
                        showEventDetail.end_datetime,
                      ).toLocaleTimeString(locale, {
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZone: displayTimeZone,
                      })}
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant uppercase tracking-wider">
                    Număr de telefon
                  </span>
                  <p className="text-sm text-on-surface-light dark:text-on-surface font-medium mt-0.5">
                    {showEventDetail.phone}
                  </p>
                </div>

                {showEventDetail.reason && (
                  <div>
                    <span className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant uppercase tracking-wider">
                      {t("calendar.reason")}
                    </span>
                    <p className="text-sm text-on-surface-light dark:text-on-surface font-medium mt-0.5">
                      {showEventDetail.reason}
                    </p>
                  </div>
                )}

                {showEventDetail.event_meta &&
                  Object.keys(showEventDetail.event_meta).length > 0 && (
                    <div>
                      <span className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant uppercase tracking-wider block mb-2">
                        {t("calendar.extraInfo")}
                      </span>
                      <div className="space-y-1.5">
                        {Object.entries(showEventDetail.event_meta).map(
                          ([k, v]) => (
                            <div
                              key={k}
                              className="flex items-center gap-2 text-sm"
                            >
                              <span className="text-on-surface-light-variant dark:text-on-surface-variant font-medium">
                                {k}:
                              </span>
                              <span className="text-on-surface-light dark:text-on-surface">
                                {v}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* Cancel event section */}
                {!showEventDetail.cancelled_at && (
                  <>
                    {showCancelConfirm ? (
                      <div className="bg-error-light/10 dark:bg-error/10 border border-error-light/20 dark:border-error/20 rounded-lg p-4">
                        <p className="text-sm text-error-light dark:text-error mb-3">
                          {t("calendar.cancelConfirm")}
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancelEvent}
                            disabled={cancelLoading}
                            className="flex-1 py-2 rounded-lg bg-error-light/20 dark:bg-error/20 border border-error-light/30 dark:border-error/30 text-error-light dark:text-error hover:bg-error-light/30 dark:hover:bg-error/30 font-semibold text-sm transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                          >
                            {cancelLoading ? <SpinnerIcon /> : null}
                            <span>
                              {cancelLoading
                                ? t("calendar.cancelling")
                                : t("calendar.yesCancel")}
                            </span>
                          </button>
                          <button
                            onClick={() => setShowCancelConfirm(false)}
                            className="flex-1 py-2 rounded-lg bg-surface-light-container dark:bg-surface-container-high border border-surface-light-highest dark:border-white/5 text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface font-semibold text-sm transition-all"
                          >
                            {t("calendar.dismiss")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowCancelConfirm(true)}
                        className="w-full py-2.5 rounded-lg bg-error-light/10 dark:bg-error/10 border border-error-light/20 dark:border-error/20 text-error-light dark:text-error hover:bg-error-light/20 dark:hover:bg-error/20 font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <MIcon name="delete" className="sm" />
                        <span>{t("calendar.cancelEvent")}</span>
                      </button>
                    )}
                  </>
                )}

                {showEventDetail.cancelled_at && (
                  <div className="bg-surface-light-container dark:bg-surface-container-high border border-surface-light-highest dark:border-white/5 rounded-lg p-3 text-center">
                    <span className="text-sm text-on-surface-light-variant dark:text-on-surface-variant font-medium">
                      {t("calendar.cancelledAt")}{" "}
                      {new Date(showEventDetail.cancelled_at).toLocaleString(
                        locale,
                        { timeZone: displayTimeZone },
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
